import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import CardFooter from '@/components/Card/CardFooter';
import Button from '@/components/CustomButtons/Button';
import {
  TemplateTypes,
  LEGACY_EMAIL_TEMPLATE_TYPES,
} from './CreateOrEditAudience';
import LetterTemplateForm from './TemplateForms/LetterTemplateForm';
import EconsentTemplateForm from './TemplateForms/EconsentTemplateForm';
import EmailTemplateForm from './TemplateForms/EmailTemplateForm';

export default function AudienceTemplates({
  templates,
  selectedTemplate,
  handleTemplateChange,
  handleTemplateChanges,
  isCreate,
  isEditingTemplates,
  setIsEditingTemplates,
  handleSubmit,
  loading,
  setTemplates,
  logoData,
  disableLegacyEmailTemplates,
}) {
  const { t } = useTranslation();
  const [originalTemplates, setOriginalTemplates] = useState(null);
  const [originalSelectedTemplate, setOriginalSelectedTemplate] = useState(
    null,
  );

  const inputsDisabled = !isCreate && !isEditingTemplates;

  const availableTemplateTypes = Object.values(TemplateTypes).filter(
    (type) =>
      !disableLegacyEmailTemplates || !LEGACY_EMAIL_TEMPLATE_TYPES.has(type),
  );

  useEffect(() => {
    if (
      selectedTemplate &&
      !availableTemplateTypes.includes(selectedTemplate)
    ) {
      handleTemplateChange({ target: { value: availableTemplateTypes[0] } });
    }
  }, [disableLegacyEmailTemplates, selectedTemplate]);

  const handleEnterEditMode = () => {
    setOriginalTemplates([...templates]);
    setOriginalSelectedTemplate(selectedTemplate);
    setIsEditingTemplates(true);
  };

  const handleCancelEdit = () => {
    if (originalTemplates) {
      setTemplates(originalTemplates);
    }
    if (originalSelectedTemplate) {
      handleTemplateChange({ target: { value: originalSelectedTemplate } });
    }
    setIsEditingTemplates(false);
  };

  const getTemplateName = (type) => {
    if (type === TemplateTypes.E_CONSENT) {
      return t('emailTemplates.audienceTemplates.eConsent');
    }
    if (type === TemplateTypes.LETTER) {
      return t('emailTemplates.audienceTemplates.letter');
    }
    if (type === TemplateTypes.ACH_RETURN_NOTIFICATION_FOR_EMAIL) {
      return t('emailTemplates.audienceTemplates.achReturnForEmail');
    }
    if (type === TemplateTypes.ACH_RETURN_NOTIFICATION_FOR_LETTER) {
      return t('emailTemplates.audienceTemplates.achReturnForLetter');
    }
    if (type === TemplateTypes.SECURE_LINK) {
      return t('emailTemplates.audienceTemplates.secureLink');
    }
    if (type === TemplateTypes.CLAIM_APPROVED) {
      return t('emailTemplates.audienceTemplates.claimApproved');
    }
    if (type === TemplateTypes.CLAIM_APPROVED_REMINDER_1) {
      return t('emailTemplates.audienceTemplates.claimApprovedReminder1');
    }
    if (type === TemplateTypes.CLAIM_APPROVED_REMINDER_2) {
      return t('emailTemplates.audienceTemplates.claimApprovedReminder2');
    }
    if (type === TemplateTypes.REFUND_PROCESSED) {
      return t('emailTemplates.audienceTemplates.refundProcessed');
    }
    if (type === TemplateTypes.REFUND_CANCELLED) {
      return t('emailTemplates.audienceTemplates.refundCancelled');
    }
    if (type === TemplateTypes.RETURN_EMAIL) {
      return t('emailTemplates.audienceTemplates.returnEmail');
    }
    if (type === TemplateTypes.RETURN_EMAIL_FOR_LETTER) {
      return t('emailTemplates.audienceTemplates.returnEmailForLetter');
    }
    return t('emailTemplates.audienceTemplates.typeEmail', { type });
  };

  const renderTemplateFormComponent = () => {
    const sharedProps = {
      formInputs: templates.find(
        (template) => template.type === selectedTemplate,
      ),
      onInputChange: (e) =>
        handleTemplateChanges(e, selectedTemplate, e.target.name),
      labelStyle: { fontSize: '12px', color: 'grey' },
      disabled: inputsDisabled,
      type: selectedTemplate,
      logoData: logoData,
    };

    switch (selectedTemplate) {
      case TemplateTypes.LETTER:
        return <LetterTemplateForm {...sharedProps} />;
      case TemplateTypes.E_CONSENT:
        return <EconsentTemplateForm {...sharedProps} />;
      default:
        return <EmailTemplateForm {...sharedProps} />;
    }
  };

  return (
    <Card>
      <CardHeader color='primary'>
        <h4>{t('emailTemplates.audienceTemplates.title')}</h4>
      </CardHeader>
      <CardBody>
        <GridContainer>
          <GridItem xs={12} sm={12} md={3}>
            <FormControl fullWidth style={{ marginBottom: '20px' }}>
              <InputLabel>
                {t('emailTemplates.audienceTemplates.selectTemplate')}
              </InputLabel>
              <Select
                value={selectedTemplate}
                onChange={handleTemplateChange}
                label={t('emailTemplates.audienceTemplates.selectTemplate')}>
                {availableTemplateTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {getTemplateName(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </GridItem>
        </GridContainer>
        {selectedTemplate && renderTemplateFormComponent()}
      </CardBody>
      {!isCreate && (
        <CardFooter style={{ justifyContent: 'flex-end' }}>
          {!isEditingTemplates && (
            <Button color='primary' onClick={handleEnterEditMode}>
              {t('emailTemplates.audienceTemplates.editButton')}
            </Button>
          )}
          {isEditingTemplates && (
            <>
              <Button
                color='rose'
                onClick={handleCancelEdit}
                style={{ marginRight: '10px' }}>
                {t('emailTemplates.audienceTemplates.cancelButton')}
              </Button>
              <Button color='primary' onClick={handleSubmit} disabled={loading}>
                {loading ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress
                      size={20}
                      color='inherit'
                      style={{ marginRight: '8px' }}
                    />
                    <span>
                      {t('emailTemplates.audienceTemplates.savingText')}
                    </span>
                  </div>
                ) : (
                  t('emailTemplates.audienceTemplates.saveButton')
                )}
              </Button>
            </>
          )}
        </CardFooter>
      )}
    </Card>
  );
}
